import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CustomerAiIntelligencePanel } from "@/components/CustomerAiIntelligencePanel";
import {
  Sparkles, History as HistoryIcon, Target, Users, ShieldAlert, Wallet, BarChart3,
  ArrowLeftRight, Tag as TagIcon, Clock, AlertTriangle, ChevronRight, Gauge, ListChecks,
} from "lucide-react";

// AI Intelligence V3 Phase 4 — Customer 360 Sales Mentor UI. The
// Experienced Sales Mentor (backend/ai_mentor.py, Phase 3) is now the
// PRIMARY AI experience; V2 (CustomerAiIntelligencePanel, unchanged) is
// embedded here as one secondary, collapsible section (G) rather than
// living in its own prominent sidebar card. Nothing about V2's own
// history/correction-flag functionality or API contract is touched.

const BAND_TONE = {
  very_low: "bg-red-500/15 text-red-600 dark:text-red-400",
  low: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
  moderate: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  high: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  very_high: "bg-emerald-600/20 text-emerald-700 dark:text-emerald-300",
  uncertain: "bg-slate-500/15 text-slate-600 dark:text-slate-400",
};
const BAND_LABEL = {
  very_low: "Very low", low: "Low", moderate: "Moderate",
  high: "High", very_high: "Very high", uncertain: "Uncertain",
};

const NOT_READY_TONE = {
  promising: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  cooling: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  unlikely: "bg-red-500/15 text-red-600 dark:text-red-400",
  dormant: "bg-slate-500/15 text-slate-600 dark:text-slate-400",
  uncertain: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
};

// 2026-09 correction: "scoring engine agrees/disagrees with Mentor" (and
// any equivalent "model alignment" concept) is REMOVED from the employee
// UI entirely — no badge, no wording, anywhere in this file. The backend
// still computes v2_agreement.status deterministically (ai_mentor.py) and
// it remains on the stored briefing doc for AI Intelligence/audit use;
// this file just never reads or renders that field anymore.

const STAT_TONE = {
  emerald: "text-emerald-600 dark:text-emerald-400",
  red: "text-red-600 dark:text-red-400",
  slate: "text-muted-foreground",
};

// Provenance badges (spec: Form / AI Voice / Consultation / Employee note /
// CRM / Payment / AI inference) — kept visually light, plain text labels.
// Keyed per evidence-checklist item (not the coarser 4-way backend
// `provenance` field) since "customer-provided" spans three visually
// distinct spec categories (Form/AI Voice/Consultation).
const EVIDENCE_KEY_PROVENANCE_LABEL = {
  intake_source: "Form", uploaded_documents: "Form",
  ai_voice_attempts: "AI Voice", ai_voice_transcript: "AI Voice",
  consultation_history: "Consultation", consultation_transcript: "Consultation",
  employee_call_notes: "Employee note", customer_profile: "CRM", payment_history: "Payment",
};

// Sales Snapshot factor bands — a plain 0-5 -> Low/Medium/High read of a
// real V2 factor score (never a second scoring engine, just a display
// transform of the same number _label_for already does for the headline
// scores). None when the factor genuinely has no evidence yet.
const FACTOR_BAND_TONE = { Low: "bg-red-500/15 text-red-600 dark:text-red-400",
  Medium: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  High: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" };
const factorBand = (factor) => {
  const score = factor?.score;
  if (score == null) return null;
  return score < 2 ? "Low" : score < 3.5 ? "Medium" : "High";
};
const CONFIDENCE_TONE = { Limited: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  Medium: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  High: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" };

// Objection nature / risk severity are deliberately FREE TEXT on the
// Mentor's own schema (never a closed enum, see ai_mentor.py's own
// docstring) — tone is a best-effort keyword read, never a source of
// truth, and always falls back to neutral for a genuinely novel label.
const objectionTone = (nature) => {
  const n = (nature || "").toLowerCase();
  if (n.includes("genuine")) return "border-red-500/40 bg-red-500/5";
  if (n.includes("soft") || n.includes("deflect")) return "border-border bg-muted/40";
  return "border-border";
};
const severityTone = (severity) => {
  const s = (severity || "").toLowerCase();
  if (s.includes("high") || s.includes("critical") || s.includes("severe")) return "border-red-500/40 bg-red-500/5";
  if (s.includes("medium") || s.includes("moderate")) return "border-amber-500/40 bg-amber-500/5";
  return "border-border";
};

const Field = ({ label, value }) => !value ? null : (
  <div>
    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-sm">{value}</p>
  </div>
);

const StatTile = ({ label, value, tone }) => (
  <div className="rounded-md border p-2">
    <p className={`text-lg font-semibold tabular-nums ${STAT_TONE[tone] || ""}`}>{value ?? 0}</p>
    <p className="text-[9px] text-muted-foreground uppercase tracking-wide">{label}</p>
  </div>
);

const MentorHistoryDrawer = ({ open, setOpen, history }) => (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="mentor-history-drawer">
      <SheetHeader>
        <SheetTitle className="font-heading flex items-center gap-2"><HistoryIcon className="h-4 w-4" />Sales Mentor History</SheetTitle>
      </SheetHeader>
      <div className="mt-4 space-y-3">
        {history.length === 0 && <p className="text-sm text-muted-foreground">No prior briefings yet.</p>}
        {history.map((h) => (
          <div key={h.briefing_id} className="rounded-md border p-3 text-xs space-y-1.5" data-testid={`mentor-history-row-${h.briefing_id}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">{fmtIST(h.generated_at)}</span>
              <Badge variant="outline" className="text-[9px]">{h.trigger_event}</Badge>
            </div>
            {h.status === "failed" ? (
              <p className="text-red-600 dark:text-red-400">Generation failed: {h.error}</p>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge className={BAND_TONE[h.briefing?.buying_likelihood_band] || ""}>
                    {BAND_LABEL[h.briefing?.buying_likelihood_band] || h.briefing?.buying_likelihood_band || "—"}
                  </Badge>
                  {h.briefing?.score_adjustment?.lead_score?.final != null && (
                    <Badge variant="outline">Lead Score {h.briefing.score_adjustment.lead_score.final}/100</Badge>
                  )}
                </div>
                <p className="text-muted-foreground line-clamp-2">{h.briefing?.overall_assessment}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);

// Compact sidebar teaser — the "quickly understand what's happening"
// entry point (page-level principle #1), kept small enough to appear
// early without scrolling even on mobile. Clicking it (or its own
// button) jumps to the full Sales Mentor tab via onOpenFull.
export const MentorSnapshotTeaser = ({ contactId, opportunityId, onOpenFull, refreshKey }) => {
  const { hasCap } = useAuth();
  const [doc, setDoc] = useState(undefined); // undefined = loading, null = none yet
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!opportunityId) { setDoc(null); return; }
    try {
      const r = await api.get(`/ai/mentor/${contactId}/${opportunityId}/briefing`);
      setDoc(r.data || null);
    } catch { setDoc(null); }
  };
  // refreshKey is bumped by the full Sales Mentor panel (a sibling, not a
  // parent/child of this teaser) after every generate/regenerate attempt,
  // so this compact summary never drifts out of sync with the full panel
  // the employee may have just used on the same page.
  useEffect(() => { setDoc(undefined); if (contactId && opportunityId) load(); }, [contactId, opportunityId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!opportunityId) return;
    setBusy(true);
    try {
      // AI Runtime & Automatic Refresh (2026-09) — the ONE canonical
      // refresh endpoint (V2 always runs first) — same as the V2 panel's
      // own generate button, so either "Generate" button on Customer 360
      // always produces a correctly-ordered pair, never a Mentor briefing
      // with "V2 not available" merely because this button was clicked
      // before V2 ever ran.
      await api.post(`/ai/opportunity/${contactId}/${opportunityId}/refresh`);
      toast.success("AI Intelligence refreshed");
      await load();
    } catch (e) {
      toast.error(errDetail(e, "AI refresh failed — CRM continues normally"));
      await load();
    } finally { setBusy(false); }
  };

  if (!opportunityId) return null;
  if (!hasCap("ai.mentor.view")) return null;
  if (doc === undefined) {
    return <Card data-testid="mentor-teaser-loading"><CardContent className="p-3"><Skeleton className="h-16" /></CardContent></Card>;
  }

  return (
    <Card data-testid="mentor-teaser" className="hover:border-[hsl(var(--info))]/50 transition-colors">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[hsl(var(--info))]" /> Sales Mentor
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {doc === null ? (
          <>
            <p className="text-xs text-muted-foreground">No Sales Mentor briefing yet for this opportunity.</p>
            {hasCap("ai.mentor.generate") && (
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                onClick={generate} data-testid="mentor-teaser-generate">
                {busy ? "Generating…" : "Generate briefing"}
              </Button>
            )}
          </>
        ) : (
          <>
            <p className="text-xs line-clamp-3" data-testid="mentor-teaser-assessment">{doc.briefing?.overall_assessment}</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className={BAND_TONE[doc.briefing?.buying_likelihood_band] || ""}>
                {BAND_LABEL[doc.briefing?.buying_likelihood_band] || doc.briefing?.buying_likelihood_band || "—"}
              </Badge>
              {doc.briefing?.not_ready_read && (
                <Badge className={NOT_READY_TONE[doc.briefing.not_ready_read.status] || ""}>
                  Not Ready — {doc.briefing.not_ready_read.status}
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">Updated {fmtIST(doc.generated_at)}</p>
            <Button size="sm" variant="ghost" className="h-7 text-xs px-0" onClick={onOpenFull} data-testid="mentor-teaser-open-full">
              View full Sales Mentor <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// The full Customer 360 -> AI / Sales Mentor area (Phase 4). onLegacyFallback
// is forwarded, untouched, straight through to the embedded V2 panel — same
// prop/contract V2 already had, just no longer occupying the page's own
// primary position.
const ACCORDION_SESSION_KEY = "mentor-accordion-open-sections";
const ACCORDION_DEFAULT_OPEN = ["understand", "objections", "commercial", "v2"];

export const SalesMentorPanel = ({ contactId, opportunityId, productName, intent, onLegacyFallback, onBriefingChanged }) => {
  const { hasCap } = useAuth();
  const [doc, setDoc] = useState(undefined); // undefined = loading, null = none yet
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);
  // Session-only collapse/expand preference (spec: "preserve for the
  // current session", not a permanent per-user setting) — one shared
  // preference across whichever customers the employee views this session.
  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = sessionStorage.getItem(ACCORDION_SESSION_KEY);
      return saved ? JSON.parse(saved) : ACCORDION_DEFAULT_OPEN;
    } catch { return ACCORDION_DEFAULT_OPEN; }
  });
  const onAccordionChange = (vals) => {
    setOpenSections(vals);
    try { sessionStorage.setItem(ACCORDION_SESSION_KEY, JSON.stringify(vals)); } catch { /* best-effort only */ }
  };

  const load = async () => {
    if (!opportunityId) { setDoc(null); return; }
    try {
      const r = await api.get(`/ai/mentor/${contactId}/${opportunityId}/briefing`);
      setDoc(r.data || null);
    } catch { setDoc(null); }
  };
  const loadHistory = async () => {
    if (!opportunityId) { setHistory([]); return; }
    try {
      const r = await api.get(`/ai/mentor/${contactId}/${opportunityId}/history`);
      setHistory(r.data || []);
    } catch { setHistory([]); }
  };

  useEffect(() => {
    setDoc(undefined); setLastError(null);
    if (contactId && opportunityId) { load(); loadHistory(); }
  }, [contactId, opportunityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!opportunityId) return;
    setBusy(true);
    try {
      // AI Runtime & Automatic Refresh (2026-09) — canonical V2-then-
      // Mentor refresh endpoint, replacing the old Mentor-only call that
      // let this button run ahead of a real V2 snapshot.
      await api.post(`/ai/opportunity/${contactId}/${opportunityId}/refresh`);
      toast.success("AI Intelligence refreshed");
      setLastError(null);
      await load(); await loadHistory();
    } catch (e) {
      // A failed regeneration must never make the previous good briefing
      // disappear — the backend never overwrites/removes it (see
      // ai_mentor.generate_mentor_briefing's own failure-isolation
      // discipline), so reloading here still shows the last successful
      // one; we only surface a non-destructive warning on top of it.
      setLastError(errDetail(e, "Mentor generation failed"));
      toast.error("Mentor regeneration failed — showing the previous briefing");
      await load(); await loadHistory();
    } finally {
      setBusy(false);
      onBriefingChanged?.(); // keep the sidebar teaser (a sibling, not a
                             // descendant) in sync either way
    }
  };

  if (!opportunityId) return null;
  if (!hasCap("ai.mentor.view")) return null;
  if (doc === undefined) {
    return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-56" /></div>;
  }

  if (doc === null) {
    return (
      <Card data-testid="mentor-panel-empty">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Sparkles className="h-4.5 w-4.5 text-[hsl(var(--info))]" /> Sales Mentor
            {productName && <Badge variant="outline" className="text-[10px]">{productName}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            No Sales Mentor briefing exists yet for this opportunity. Generate one to get a clear read
            on this customer — what's happening, how ready they are, and what to ask/say next.
          </p>
          {hasCap("ai.mentor.generate") && (
            <Button data-testid="mentor-generate-button" size="sm" disabled={busy} onClick={generate}>
              {busy ? "Generating…" : "Generate Sales Mentor briefing"}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const b = doc.briefing || {};
  const cu = b.customer_understanding || {};
  const ca = b.commercial_analysis || {};
  const cg = b.consultant_guidance || {};
  const hl = b.historical_learning || {};
  const facts = hl.observed_facts || {};
  const interp = hl.mentor_interpretation || {};
  const notReady = b.not_ready_read;
  const objections = b.objections || [];
  const risks = b.risks || [];
  const topics = b.topics || [];
  const otherObs = b.other_observations || [];
  const scores = doc.v2_scores || {};
  // The final, employee-facing numbers — base score with any bounded,
  // evidence-based adjustment already applied (2026-09 correction).
  // final == base whenever no adjustment reasons apply, always.
  const adj = doc.score_adjustment || {};
  const helpingReasons = (adj.reasons || []).filter((r) => r.delta > 0);
  const holdingReasons = (adj.reasons || []).filter((r) => r.delta < 0);
  const evidence = doc.evidence_summary;
  const engagementBand = evidence == null ? null
    : evidence.interaction_count >= 3 ? "High" : evidence.interaction_count >= 1 ? "Medium" : "Low";

  return (
    <div className="space-y-4" data-testid="mentor-panel">
      {/* Sales Snapshot — the commercial headline, first thing an employee
          sees. Lead Score/Closing Probability are the FINAL employee-
          facing numbers: a fixed deterministic baseline (V2) with a
          bounded, explainable evidence adjustment already applied (never
          shown separately as two competing numbers — see score_adjustment
          below for the "why"). */}
      <Card data-testid="mentor-sales-snapshot" className="border-[hsl(var(--info))]/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Gauge className="h-4 w-4" />Sales Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            <div className="rounded-md border p-2.5" data-testid="snapshot-lead-score">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Lead Score</p>
              <p className="text-lg font-semibold tabular-nums">
                {adj.lead_score?.final != null ? `${adj.lead_score.final}/100` : "—"}
              </p>
              <p className="text-xs text-muted-foreground">{adj.lead_score?.label || "Cannot determine"}</p>
            </div>
            <div className="rounded-md border p-2.5" data-testid="snapshot-closing-probability">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Closing Probability</p>
              <p className="text-lg font-semibold tabular-nums">
                {adj.closing_probability?.final != null ? `${adj.closing_probability.final}%`
                  : scores.closing_probability?.state === "converted" ? "Converted"
                  : scores.closing_probability?.state === "lost" ? "Lost" : "—"}
              </p>
              <p className="text-xs text-muted-foreground">{adj.closing_probability?.temperature || "Unknown"}</p>
            </div>
            <div className="rounded-md border p-2.5" data-testid="snapshot-intent">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Intent</p>
              <p className="text-sm font-medium capitalize mt-1.5">{intent ? intent.replace(/_/g, " ") : "Not set"}</p>
            </div>
            <div className="rounded-md border p-2.5" data-testid="snapshot-urgency">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Urgency</p>
              {factorBand(scores.urgency_factor) ? (
                <Badge className={`mt-1.5 ${FACTOR_BAND_TONE[factorBand(scores.urgency_factor)]}`}>{factorBand(scores.urgency_factor)}</Badge>
              ) : <p className="text-sm text-muted-foreground mt-1.5">Unknown</p>}
            </div>
            <div className="rounded-md border p-2.5" data-testid="snapshot-budget">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Budget Readiness</p>
              {factorBand(scores.budget_factor) || factorBand(scores.funds_factor) ? (
                <Badge className={`mt-1.5 ${FACTOR_BAND_TONE[factorBand(scores.budget_factor) || factorBand(scores.funds_factor)]}`}>
                  {factorBand(scores.budget_factor) || factorBand(scores.funds_factor)}
                </Badge>
              ) : <p className="text-sm text-muted-foreground mt-1.5">Needs verification</p>}
            </div>
            <div className="rounded-md border p-2.5" data-testid="snapshot-engagement">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Engagement</p>
              {engagementBand && (
                <Badge className={`mt-1.5 ${FACTOR_BAND_TONE[engagementBand]}`}>{engagementBand}</Badge>
              )}
            </div>
            <div className="rounded-md border p-2.5" data-testid="snapshot-evidence-confidence">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Evidence Confidence</p>
              {evidence && (
                <Badge className={`mt-1.5 ${CONFIDENCE_TONE[evidence.confidence] || ""}`}>{evidence.confidence}</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business-language score explanation (2026-09 correction) — the
          exact reasons behind any adjustment away from the baseline,
          never raw model text and never a second number. Empty both ways
          means the score is exactly the deterministic baseline. */}
      {(helpingReasons.length > 0 || holdingReasons.length > 0) && (
        <Card data-testid="mentor-score-explanation">
          <CardContent className="pt-4 grid sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wide font-medium mb-1">Helping the score</p>
              {helpingReasons.length === 0 ? <p className="text-xs text-muted-foreground">Nothing specific identified.</p> : (
                <ul className="text-sm space-y-0.5" data-testid="score-helping-list">
                  {helpingReasons.map((r) => <li key={r.code}>{r.label}</li>)}
                </ul>
              )}
            </div>
            <div>
              <p className="text-[10px] text-amber-600 dark:text-amber-400 uppercase tracking-wide font-medium mb-1">Holding it back</p>
              {holdingReasons.length === 0 ? <p className="text-xs text-muted-foreground">Nothing specific identified.</p> : (
                <ul className="text-sm space-y-0.5" data-testid="score-holding-list">
                  {holdingReasons.map((r) => <li key={r.code}>{r.label}</li>)}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Evidence used — what SalesOS actually had on hand when this
          briefing was generated. Never implies something exists when it
          doesn't; items unavailable today are shown, not hidden. */}
      {evidence && (
        <Card data-testid="mentor-evidence-used">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <ListChecks className="h-4 w-4" />Evidence used for this briefing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {evidence.items.map((it) => (
                <div key={it.key} data-testid={`evidence-item-${it.key}`}>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className={it.available ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}>
                      {it.available ? "●" : "○"}
                    </span>
                    <span className={it.available ? "" : "text-muted-foreground"}>{it.label}</span>
                    <span className="text-[9px] text-muted-foreground border rounded px-1">{EVIDENCE_KEY_PROVENANCE_LABEL[it.key] || it.provenance}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      {it.state === "none" ? "none"
                        : it.key === "uploaded_documents" && it.available ? "present, not analysed"
                        : it.available ? "available" : "not available"}
                    </span>
                  </div>
                  {it.note && <p className="text-[10px] text-muted-foreground pl-4">{it.note}</p>}
                </div>
              ))}
            </div>
            <div className="pt-1.5 border-t text-xs space-y-0.5">
              <p>This briefing is primarily based on: <span className="font-medium">{evidence.primarily_based_on}</span></p>
              <p className="text-muted-foreground">Evidence confidence: {evidence.confidence} ({evidence.available_count} of {evidence.total_count} primary sources)</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* A. Mentor Snapshot */}
      <Card data-testid="mentor-snapshot">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2 flex-wrap">
            <Sparkles className="h-4.5 w-4.5 text-[hsl(var(--info))]" /> Sales Mentor
            {productName && <Badge variant="outline" className="text-[10px]">{productName}</Badge>}
            <Badge variant="outline" className="text-[10px]">Advisory only</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {lastError && (
            <Alert className="border-amber-500/40 bg-amber-500/5 py-2" data-testid="mentor-regen-failed-banner">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-xs text-amber-700 dark:text-amber-400">
                Latest regeneration failed. Showing previous successful Mentor briefing.
              </AlertDescription>
            </Alert>
          )}
          <p className="text-sm leading-relaxed" data-testid="mentor-overall-assessment">{b.overall_assessment}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Buying likelihood</span>
              <Badge className={BAND_TONE[b.buying_likelihood_band] || ""} data-testid="mentor-band">
                {BAND_LABEL[b.buying_likelihood_band] || b.buying_likelihood_band || "—"}
              </Badge>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Confidence</span>
              <span className="text-sm">{b.confidence || "—"}</span>
            </div>
            {notReady && (
              <Badge className={NOT_READY_TONE[notReady.status] || ""} data-testid="mentor-not-ready-badge">
                Not Ready — {notReady.status}
              </Badge>
            )}
          </div>
          {b.buying_likelihood && <p className="text-xs text-muted-foreground">{b.buying_likelihood}</p>}
          {(b.reasons_to_signup || b.reasons_against_signup) && (
            <div className="grid sm:grid-cols-2 gap-2 text-xs">
              {b.reasons_to_signup && (
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2">
                  <p className="font-medium text-emerald-700 dark:text-emerald-400 mb-0.5">Why they might Signup</p>
                  <p className="text-muted-foreground">{b.reasons_to_signup}</p>
                </div>
              )}
              {b.reasons_against_signup && (
                <div className="rounded-md border border-red-500/30 bg-red-500/5 p-2">
                  <p className="font-medium text-red-700 dark:text-red-400 mb-0.5">Why they might not</p>
                  <p className="text-muted-foreground">{b.reasons_against_signup}</p>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center justify-between pt-1 flex-wrap gap-2">
            <p className="text-[10px] text-muted-foreground">
              Generated {fmtIST(doc.generated_at)} · {doc.model || "—"} · Mentor can be wrong — verify against records
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setHistoryOpen(true)} data-testid="mentor-history-button">
                <HistoryIcon className="h-3.5 w-3.5 mr-1" />History
              </Button>
              {hasCap("ai.mentor.generate") && (
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={generate} data-testid="mentor-regenerate-button">
                  {busy ? "Regenerating…" : "Regenerate"}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* B. Recommended Next Action — the most visually prominent section */}
      <Card className="border-[hsl(var(--info))]/40" data-testid="mentor-next-action">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Target className="h-4 w-4" />Recommended Next Action
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5">
          <div className="grid sm:grid-cols-2 gap-2.5">
            <Field label="What to ask next" value={cg.what_to_ask_next} />
            <Field label="What to say next" value={cg.what_to_say_next} />
          </div>
          {cg.what_not_to_say && (
            <div className="rounded-md border border-red-500/40 bg-red-500/5 p-2 text-sm" data-testid="mentor-what-not-to-say">
              <p className="text-[10px] uppercase tracking-wide text-red-600 dark:text-red-400 font-medium mb-0.5">Do NOT say</p>
              {cg.what_not_to_say}
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-2.5">
            <Field label="Commitment to seek" value={cg.recommended_next_commitment} />
            <Field label="Follow-up timing" value={cg.follow_up_timing} />
            <Field label="Follow-up channel" value={cg.follow_up_channel} />
          </div>
          {cg.talking_points?.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Talking points</p>
              <ul className="text-sm list-disc pl-4 space-y-0.5">
                {cg.talking_points.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Not Ready detail (only when currently relevant) — expands on the
          compact Snapshot badge with the Mentor's own aged commercial read */}
      {notReady && (
        <Card className="border-amber-500/40" data-testid="mentor-not-ready-detail">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <Clock className="h-4 w-4" />Not Ready — {notReady.status}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <p>{notReady.reasoning}</p>
            <p className="text-xs text-muted-foreground">Confidence: {notReady.confidence}</p>
            <p><span className="text-muted-foreground">Recommended action:</span> {notReady.recommended_action}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-4">
          <Accordion type="multiple" value={openSections} onValueChange={onAccordionChange} className="w-full">
            {/* C. Understand the Customer */}
            <AccordionItem value="understand">
              <AccordionTrigger className="text-xs py-2">
                <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Understand the Customer</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-2">
                <Field label="Goals" value={cu.goals} />
                <Field label="Motivations" value={cu.motivations} />
                <Field label="Genuine curiosity" value={cu.genuine_curiosity} />
                <Field label="Buying signals" value={cu.buying_signals} />
                <Field label="Decision makers" value={cu.decision_makers} />
                <Field label="Concerns" value={cu.concerns} />
                <Field label="Contradictions" value={cu.contradictions} />
                <Field label="Missing information" value={cu.missing_information} />
                {b.possible_hidden_concern && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 font-medium mb-0.5">
                      Possible hidden concern
                    </p>
                    <p className="text-sm">{b.possible_hidden_concern}</p>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* D. Objections & Risks — every compound concern preserved as its
                own independent item (never merged), per Phase 3's structural
                atomic-evidence-extraction fix */}
            <AccordionItem value="objections">
              <AccordionTrigger className="text-xs py-2">
                <span className="flex items-center gap-1.5">
                  <ShieldAlert className="h-3.5 w-3.5" />Objections & Risks ({objections.length + risks.length})
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                <div className="text-[10px] text-muted-foreground flex items-start gap-1.5">
                  <Badge variant="outline" className="text-[9px] shrink-0">AI inference</Badge>
                  <span>The Sales Mentor's own read of the evidence below — "Supporting evidence" quotes what the customer actually said; the objection/risk framing itself is the Mentor's synthesis, not a verbatim customer statement.</span>
                </div>
                {objections.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Objections</p>
                    {objections.map((o, i) => (
                      <div key={i} className={`rounded-md border p-2 text-sm ${objectionTone(o.nature)}`} data-testid={`mentor-objection-${i}`}>
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <Badge variant="outline" className="text-[9px]">{o.topic}</Badge>
                          <span className="text-[10px] text-muted-foreground">{o.nature}</span>
                        </div>
                        {o.supporting_evidence && <p className="text-muted-foreground text-xs">{o.supporting_evidence}</p>}
                        {o.hidden_concern_hint && <p className="text-xs italic mt-0.5">Hidden concern hint: {o.hidden_concern_hint}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {risks.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Risks</p>
                    {risks.map((r, i) => (
                      <div key={i} className={`rounded-md border p-2 text-sm ${severityTone(r.severity)}`} data-testid={`mentor-risk-${i}`}>
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <Badge variant="outline" className="text-[9px]">{r.category}</Badge>
                          <Badge variant="outline" className="text-[9px] capitalize">{r.severity}</Badge>
                        </div>
                        {r.description}
                      </div>
                    ))}
                  </div>
                )}
                {objections.length === 0 && risks.length === 0 && (
                  <p className="text-xs text-muted-foreground">No objections or risks identified.</p>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* E. Commercial Read */}
            <AccordionItem value="commercial">
              <AccordionTrigger className="text-xs py-2">
                <span className="flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" />Commercial Read</span>
              </AccordionTrigger>
              <AccordionContent className="grid sm:grid-cols-2 gap-2">
                <Field label="Budget realism" value={ca.budget_realism} />
                <Field label="Funds readiness" value={ca.funds_readiness} />
                <Field label="Urgency" value={ca.urgency} />
                <Field label="Competition situation" value={ca.competition_situation} />
                <Field label="Product understanding" value={ca.product_understanding} />
                <Field label="Readiness assessment" value={ca.readiness_assessment} />
              </AccordionContent>
            </AccordionItem>

            {/* F. Aussie Pathways Historical Evidence — the Phase-3 provenance
                boundary preserved visually: Observed Facts (backend-owned,
                its own labeled block) is NEVER blended into Mentor
                Interpretation (a visually separate box below it). */}
            <AccordionItem value="historical">
              <AccordionTrigger className="text-xs py-2">
                <span className="flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5" />Aussie Pathways Historical Evidence</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                {facts.material_match_count > 0 ? (
                  <>
                    <div data-testid="mentor-observed-facts">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5">Observed Aussie Pathways Evidence</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <StatTile label="Material matches" value={facts.material_match_count} />
                        <StatTile label="Signup" value={facts.signup_count} tone="emerald" />
                        <StatTile label="Lost / Discontinued" value={facts.lost_count} tone="red" />
                        <StatTile label="Still open" value={facts.still_open_count} tone="slate" />
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                        <span>Closed-only conversion rate: {facts.closed_conversion_rate != null ? `${Math.round(facts.closed_conversion_rate * 100)}%` : "not yet available (all matches still open)"}</span>
                        <span>Historical confidence: {facts.historical_confidence || "—"}</span>
                      </div>
                      {facts.per_match_summary?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {facts.per_match_summary.map((m, i) => (
                            <Badge key={i} variant="outline" className="text-[9px]"
                              title={`Ranking score ${Math.round((m.ranking_score || 0) * 100)}% · Evidence coverage ${Math.round((m.evidence_coverage || 0) * 100)}%`}>
                              {m.candidate_ref}: {(m.outcome_bucket || "").replace("_", " ")}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="rounded-md border p-2.5 bg-muted/30" data-testid="mentor-historical-interpretation">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Mentor Interpretation</p>
                      <p className="text-sm">{interp.pattern_summary}</p>
                      {interp.evidence_sufficiency_note && <p className="text-xs text-muted-foreground mt-1">{interp.evidence_sufficiency_note}</p>}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="mentor-no-historical-evidence">
                    No materially similar historical Aussie Pathways customers found yet — historical
                    evidence isn't available for this comparison.
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* G. Supporting Evidence — the automatic scoring system's own
                factor-level detail, secondary to the Mentor's own read
                above. 2026-09 correction: no "agrees/disagrees with the
                automatic score" concept shown here at all anymore — that
                comparison stays internal (ai_mentor.py's own
                v2_agreement, still computed for audit) and never reaches
                this employee-facing panel. */}
            <AccordionItem value="v2">
              <AccordionTrigger className="text-xs py-2">
                <span className="flex items-center gap-1.5"><ArrowLeftRight className="h-3.5 w-3.5" />Supporting Evidence</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                <Badge variant="outline" className="text-[9px]">AI inference</Badge>
                <CustomerAiIntelligencePanel contactId={contactId} opportunityId={opportunityId}
                  productName={productName} onLegacyFallback={onLegacyFallback} />
              </AccordionContent>
            </AccordionItem>

            {/* H. Topics / Other Observations — free-form, never forced into a
                predefined UI category */}
            <AccordionItem value="topics">
              <AccordionTrigger className="text-xs py-2">
                <span className="flex items-center gap-1.5"><TagIcon className="h-3.5 w-3.5" />Topics & Other Observations</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-2">
                {topics.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {topics.map((t, i) => <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>)}
                  </div>
                )}
                {otherObs.length > 0 && (
                  <ul className="text-sm list-disc pl-4 space-y-0.5">
                    {otherObs.map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                )}
                {topics.length === 0 && otherObs.length === 0 && (
                  <p className="text-xs text-muted-foreground">No additional topics or observations.</p>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <MentorHistoryDrawer open={historyOpen} setOpen={setHistoryOpen} history={history} />
    </div>
  );
};
